import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { OAuthButtons } from '@/components/auth/OAuthButtons';
import { EmailVerification } from '@/components/auth/EmailVerification';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/providers/AuthProvider';

export function RegisterPage() {
 const { toast } = useToast();
 const [formData, setFormData] = useState({
  email: '',
  password: '',
  confirmPassword: '',
  fullName: '',
 });
 const [isLoading, setIsLoading] = useState(false);
 const [showVerification, setShowVerification] = useState(false);
 const [registeredEmail, setRegisteredEmail] = useState('');
 const { user, register } = useAuth();

 // Redirect if already logged in
 if (user) {
  return <Navigate to='/dashboard' replace />;
 }

 const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  setFormData((prev) => ({
   ...prev,
   [e.target.name]: e.target.value,
  }));
 };

 const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();

  if (formData.password !== formData.confirmPassword) {
   toast({
    title: 'Error',
    description: 'Passwords do not match',
    variant: 'destructive',
   });
   return;
  }

  if (formData.password.length < 8) {
   toast({
    title: 'Error',
    description: 'Password must be at least 8 characters long',
    variant: 'destructive',
   });
   return;
  }

  setIsLoading(true);
  try {
   const result = await register(
    formData.email,
    formData.password,
    formData.fullName
   );

   if (result.needsVerification) {
    setRegisteredEmail(formData.email);
    setShowVerification(true);
   }
  } catch (error) {
   // Error is handled by AuthProvider
  } finally {
   setIsLoading(false);
  }
 };

 const handleVerificationSuccess = () => {
  // User will be automatically redirected by AuthProvider after successful verification
 };

 if (showVerification) {
  return (
   <div className='min-h-screen flex items-center justify-center bg-background py-12 px-4'>
    <div className='max-w-md w-full border border-border p-8 bg-secondary/10'>
     <div className='font-mono text-center mb-8 text-primary'>
      // VERIFICATION REQUIRED
     </div>
     <EmailVerification
      email={registeredEmail}
      onSuccess={handleVerificationSuccess}
     />
    </div>
   </div>
  );
 }

 return (
  <div className='min-h-screen w-full grid grid-cols-1 lg:grid-cols-2'>
   {/* Left Panel: Initialization Visual */}
   <div className='hidden lg:flex flex-col justify-between bg-secondary/10 border-r border-border p-12 relative overflow-hidden'>
    {/* Background Grid */}
    <div className='absolute inset-0 bg-[radial-gradient(#27272a_1px,transparent_1px)] [background-size:16px_16px] opacity-20 pointer-events-none'></div>

    <div className='relative z-10'>
     <div className='font-mono font-bold text-xl flex items-center gap-2 mb-12'>
      <div className='w-3 h-3 bg-primary animate-pulse' />
      MOCK.
     </div>
     <h1 className='text-6xl font-bold tracking-tighter leading-tight'>
      NEW
      <br />
      ACCOUNT
      <br />
      SETUP
     </h1>
    </div>

    <div className='relative z-10 font-mono text-sm text-muted-foreground space-y-4 font-bold opacity-80'>
     <p>&gt; verifying_requirements... OK</p>
     <p>&gt; establishing_secure_channel... OK</p>
     <p>
      &gt; waiting_for_user_input... <span className='animate-pulse'>_</span>
     </p>
    </div>
   </div>

   {/* Right Panel: Registration Form */}
   <div className='flex flex-col justify-between p-6 lg:p-12 bg-background'>
    <div className='flex justify-end'>
     <ThemeToggle />
    </div>

    <div className='max-w-md w-full mx-auto space-y-8'>
     <div className='space-y-2'>
      <h2 className='text-3xl font-bold tracking-tight'>Sign Up</h2>
      <p className='text-muted-foreground font-mono text-sm'>
       Create your professional profile
      </p>
     </div>

     <div className='space-y-6'>
      <OAuthButtons />

      <div className='relative'>
       <div className='absolute inset-0 flex items-center'>
        <span className='w-full border-t border-border' />
       </div>
       <div className='relative flex justify-center text-xs uppercase'>
        <span className='bg-background px-2 text-muted-foreground font-mono'>
         Or register with email
        </span>
       </div>
      </div>

      <form onSubmit={handleSubmit} className='space-y-4'>
       <div className='space-y-2'>
        <Label
         htmlFor='fullName'
         className='font-mono text-xs uppercase tracking-wider'
        >
         Full Name
        </Label>
        <Input
         id='fullName'
         name='fullName'
         type='text'
         placeholder='Jane Doe'
         value={formData.fullName}
         onChange={handleInputChange}
         className='h-12 font-mono'
        />
       </div>

       <div className='space-y-2'>
        <Label
         htmlFor='email'
         className='font-mono text-xs uppercase tracking-wider'
        >
         Email
        </Label>
        <Input
         id='email'
         name='email'
         type='email'
         placeholder='name@company.com'
         value={formData.email}
         onChange={handleInputChange}
         className='h-12 font-mono'
         required
        />
       </div>

       <div className='grid grid-cols-2 gap-4'>
        <div className='space-y-2'>
         <Label
          htmlFor='password'
          className='font-mono text-xs uppercase tracking-wider'
         >
          Password
         </Label>
         <Input
          id='password'
          name='password'
          type='password'
          placeholder='••••••••'
          value={formData.password}
          onChange={handleInputChange}
          className='h-12 font-mono'
          required
         />
        </div>
        <div className='space-y-2'>
         <Label
          htmlFor='confirmPassword'
          className='font-mono text-xs uppercase tracking-wider'
         >
          Confirm
         </Label>
         <Input
          id='confirmPassword'
          name='confirmPassword'
          type='password'
          placeholder='••••••••'
          value={formData.confirmPassword}
          onChange={handleInputChange}
          className='h-12 font-mono'
          required
         />
        </div>
       </div>

       <Button
        type='submit'
        className='w-full h-12 text-base font-bold dark:text-black'
        disabled={isLoading}
       >
        {isLoading ? (
         <span className='flex items-center gap-2 font-mono'>
          <div className='w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin' />
          CREATING ACCOUNT...
         </span>
        ) : (
         'CREATE ACCOUNT'
        )}
       </Button>
      </form>

      <p className='text-center text-sm text-muted-foreground'>
       Already have an account?{' '}
       <Link to='/login' className='text-primary hover:underline font-mono'>
        Sign In
       </Link>
      </p>
     </div>
    </div>

    <div className='font-mono text-xs text-muted-foreground text-center lg:text-left pt-8'>
     Secure Connection // TLS 1.3
    </div>
   </div>
  </div>
 );
}
